import React, {useEffect, useMemo, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Box, Button, ButtonGroup, Stack, Typography, alpha, useTheme} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faAngleLeft, faAngleRight, faVideo} from '@fortawesome/pro-solid-svg-icons';
import dayjs, {Dayjs} from 'dayjs';
import {SchedulerEvent} from '@mui/x-scheduler/models';

import {CalendarEventDetail, SOURCE_O365} from './calendarSources';
import {CATEGORY_COLOR, PlannerCategory} from './plannerModel';

interface Props {
    /** Dieselben Termine wie im Raster — die Liste ist eine andere Darstellung, keine andere Quelle. */
    events: SchedulerEvent[];
    /** Rohdaten je Termin-Id — für den Detail-Dialog. */
    details: Map<string, CalendarEventDetail>;
    /** Der Monat, der gezeigt wird. */
    visibleDate: Dayjs;
    onVisibleDateChange: (next: Dayjs) => void;
    onSelect: (detail: CalendarEventDetail) => void;
}

/**
 * Die Farbe eines Termins aus seiner Herkunft.
 *
 * Dieselben Farben wie in der Planungstafel — wer beide Ansichten benutzt, soll eine Abwesenheit
 * nicht zweimal lernen müssen.
 */
const colorOf = (
    resource: string | string[] | null | undefined,
    detail: CalendarEventDetail | undefined,
): string => {
    // Der Baustein lässt auch mehrere Herkünfte je Termin zu; unsere Zuordnung ist eindeutig, und
    // die erste ist damit die richtige.
    const source = Array.isArray(resource) ? resource[0] : resource;

    if (source === SOURCE_O365 || detail?.kind === 'outlook') {
        return detail?.kind === 'outlook' && detail.event.showAs === 'free'
            ? CATEGORY_COLOR.outlookFree
            : CATEGORY_COLOR.outlook;
    }

    const category = (source?.split('.')[1] ?? 'other') as PlannerCategory;
    return CATEGORY_COLOR[category] ?? CATEGORY_COLOR.other;
};

/** Die zweite Zeile eines Eintrags: was den Termin greifbar macht, ohne ihn zu öffnen. */
const subtitleOf = (detail: CalendarEventDetail | undefined): string | undefined => {
    if (!detail) return undefined;

    if (detail.kind === 'outlook') {
        return [detail.event.location, detail.event.organizer].filter(Boolean).join(' · ') || undefined;
    }

    if (detail.kind === 'entry') {
        const order = detail.entry.order;
        return [order?.customerName, order?.subject ?? detail.entry.note].filter(Boolean).join(' · ')
            || undefined;
    }

    return detail.task.activity ?? undefined;
};

/**
 * Die Terminliste für Telefone.
 *
 * **Warum eigenständig und nicht der Kalenderbaustein:** `EventCalendar` von MUI X ist auf schmalen
 * Geräten nicht zu gebrauchen — die Datumsspalte nimmt die halbe Breite, der Betreff daneben wird
 * abgeschnitten, und jeder leere Tag verbraucht trotzdem eine volle Zeile. Auf den rund 290 Punkten
 * eines iPhone SE bleibt davon kein lesbarer Termin übrig, und über CSS ist an den Innereien des
 * Bausteins nicht verlässlich heranzukommen (die Klassennamen sind unstabil, siehe
 * `findClickedEventId`).
 *
 * Deshalb hier die drei Entscheidungen, die die Liste brauchbar machen:
 *
 * 1. **Leere Tage entfallen.** Sie sind die Antwort auf eine Frage, die niemand stellt, und auf
 *    einem Telefon kostet jede Zeile ein Sechstel des Bildschirms.
 * 2. **Volle Breite für den Betreff.** Das Datum steht einmal als Überschrift über seiner Gruppe,
 *    nicht neben jedem Eintrag.
 * 3. **Die Uhrzeit führt.** Sie ist beim Durchsehen das Ordnungsmerkmal, nicht der Betreff.
 */
export const MobileAgenda: React.FC<Props> = ({
    events, details, visibleDate, onVisibleDateChange, onSelect,
}) => {
    const {t, i18n} = useTranslation();
    const theme = useTheme();

    const today = dayjs();
    const todayKey = today.format('YYYY-MM-DD');

    /**
     * Blickt der gezeigte Monat in die Vergangenheit, wird er vollständig gezeigt.
     *
     * Wer bewusst zurückblättert, will das Vergangene sehen — dort alles auszublenden ergäbe eine
     * leere Seite und den Eindruck, es gäbe keine Daten.
     */
    const showPast = visibleDate.isBefore(today, 'month');

    /** Nach Tagen gruppiert; leere Tage und Vergangenes fallen heraus. */
    const days = useMemo(() => {
        const groups = new Map<string, SchedulerEvent[]>();

        for (const event of events) {
            const startKey = String(event.start).slice(0, 10);
            const endKey = String(event.end ?? event.start).slice(0, 10);

            // Abgelaufenes weglassen: im laufenden Monat beginnt die Liste bei heute, nicht am
            // Monatsersten. Was vorbei ist, ist keine Planung mehr.
            if (!showPast && endKey < todayKey) continue;

            // Etwas, das vor heute begann und noch läuft (etwa ein mehrtägiger Outlook-Termin),
            // gehört unter **heute**. Unter seinem Anfangstag stünde es hinter der Grenze und wäre
            // damit unsichtbar — obwohl es gerade läuft.
            const key = !showPast && startKey < todayKey ? todayKey : startKey;

            const list = groups.get(key);
            if (list) list.push(event);
            else groups.set(key, [event]);
        }

        // forEach statt Spread über den Map-Iterator: das Übersetzungsziel des Projekts kann sie
        // nicht abbilden (dieselbe Einschränkung wie an anderen Stellen im Client).
        const collected: {key: string; day: Dayjs; events: SchedulerEvent[]}[] = [];
        groups.forEach((list, key) => collected.push({
            key,
            day: dayjs(key),
            // Ganztägiges zuerst — es rahmt den Tag, statt in ihm zu liegen.
            events: list.sort((a, b) => {
                if ((a.allDay ?? false) !== (b.allDay ?? false)) return a.allDay ? -1 : 1;
                return String(a.start).localeCompare(String(b.start));
            }),
        }));

        return collected.sort((a, b) => a.key.localeCompare(b.key));
    }, [events, showPast, todayKey]);

    const listRef = useRef<HTMLDivElement | null>(null);

    // Beim Monatswechsel oben beginnen. Ein Sprung auf heute ist nicht mehr nötig — heute ist im
    // laufenden Monat der erste Eintrag.
    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
    }, [visibleDate]);

    return (
        <Box>
            <Stack direction="row" spacing={1} sx={{
                px: 1, py: 1, alignItems: 'center', justifyContent: 'space-between',
                borderBottom: 1, borderColor: 'divider',
            }}>
                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                    {visibleDate.toDate().toLocaleDateString(i18n.language,
                        {month: 'long', year: 'numeric'})}
                </Typography>

                <ButtonGroup size="small" variant="outlined">
                    <Button onClick={() => onVisibleDateChange(visibleDate.subtract(1, 'month'))}
                            aria-label={t('calendar.previous')}>
                        <FontAwesomeIcon icon={faAngleLeft}/>
                    </Button>
                    <Button onClick={() => onVisibleDateChange(dayjs().startOf('day'))}>
                        {t('calendar.today')}
                    </Button>
                    <Button onClick={() => onVisibleDateChange(visibleDate.add(1, 'month'))}
                            aria-label={t('calendar.next')}>
                        <FontAwesomeIcon icon={faAngleRight}/>
                    </Button>
                </ButtonGroup>
            </Stack>

            {/*
              * Eigener Rollbereich: die Monatsnavigation bleibt oben stehen, und die haftenden
              * Tagesüberschriften haben einen Behälter, an dem sie haften können.
              */}
            <Box ref={listRef} sx={{
                overflowY: 'auto',
                maxHeight: 'calc(100dvh - 250px)',
                minHeight: 280,
                WebkitOverflowScrolling: 'touch',
            }}>
            {days.length === 0 ? (
                <Box sx={{px: 2, py: 5, textAlign: 'center'}}>
                    <Typography variant="body2" color="text.secondary">
                        {t('calendar.agendaEmpty')}
                    </Typography>
                </Box>
            ) : days.map(({key, day, events: dayEvents}) => {
                const isToday = day.isSame(today, 'day');

                return (
                    <Box key={key}>
                        {/* Das Datum einmal je Gruppe — und beim Blättern am oberen Rand haftend. */}
                        <Stack direction="row" spacing={1} sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                            alignItems: 'baseline',
                            px: 1.5,
                            py: 0.75,
                            bgcolor: isToday
                                ? alpha(theme.palette.primary.main, 0.1)
                                : 'action.hover',
                            borderTop: 1,
                            borderColor: 'divider',
                        }}>
                            <Typography variant="subtitle2" sx={{
                                fontWeight: 700,
                                color: isToday ? 'primary.main' : 'text.primary',
                            }}>
                                {day.toDate().toLocaleDateString(i18n.language,
                                    {weekday: 'short', day: '2-digit', month: '2-digit'})}
                            </Typography>
                            {isToday && (
                                <Typography variant="caption" sx={{fontWeight: 700, color: 'primary.main'}}>
                                    {t('calendar.today')}
                                </Typography>
                            )}
                        </Stack>

                        {dayEvents.map((event) => {
                            const detail = details.get(String(event.id));
                            const color = colorOf(event.resource, detail);
                            const subtitle = subtitleOf(detail);
                            const online = detail?.kind === 'outlook' && (detail.event.isOnlineMeeting ?? false);

                            return (
                                <Stack key={String(event.id)} direction="row" spacing={1.25}
                                       onClick={() => detail && onSelect(detail)}
                                       sx={{
                                           px: 1.5,
                                           py: 1,
                                           alignItems: 'flex-start',
                                           borderTop: 1,
                                           borderColor: 'divider',
                                           borderLeft: '4px solid',
                                           borderLeftColor: color,
                                           cursor: detail ? 'pointer' : 'default',
                                           // Fingerkuppe statt Mauszeiger: unter 44 Punkten Höhe
                                           // trifft man auf einem Telefon daneben.
                                           minHeight: 44,
                                           '&:active': {bgcolor: alpha(color, 0.12)},
                                       }}>
                                    {/*
                                      * Die Uhrzeit in fester Breite und mit Tabellenziffern: so
                                      * stehen alle Zeiten des Tages untereinander auf einer Kante,
                                      * und das Auge findet die Lücken.
                                      */}
                                    <Typography variant="caption" sx={{
                                        minWidth: 42,
                                        flexShrink: 0,
                                        pt: '2px',
                                        fontWeight: 600,
                                        fontVariantNumeric: 'tabular-nums',
                                        color: 'text.secondary',
                                        lineHeight: 1.35,
                                    }}>
                                        {event.allDay
                                            ? t('calendar.allDay')
                                            : dayjs(event.start).format('HH:mm')}
                                    </Typography>

                                    <Box sx={{minWidth: 0, flexGrow: 1}}>
                                        <Stack direction="row" spacing={0.75} sx={{alignItems: 'center'}}>
                                            {online && (
                                                <Box component="span" sx={{
                                                    color, display: 'inline-flex', fontSize: '0.7rem',
                                                    flexShrink: 0,
                                                }}>
                                                    <FontAwesomeIcon icon={faVideo}/>
                                                </Box>
                                            )}
                                            <Typography variant="body2" sx={{
                                                fontWeight: 600,
                                                lineHeight: 1.35,
                                                // Zwei Zeilen für den Betreff: eine schneidet zu
                                                // oft mitten im entscheidenden Wort ab.
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}>
                                                {event.title}
                                            </Typography>
                                        </Stack>

                                        {subtitle && (
                                            <Typography variant="caption" color="text.secondary" sx={{
                                                display: 'block',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {subtitle}
                                            </Typography>
                                        )}
                                    </Box>

                                    {!event.allDay && (
                                        <Typography variant="caption" color="text.secondary" sx={{
                                            flexShrink: 0,
                                            pt: '2px',
                                            fontVariantNumeric: 'tabular-nums',
                                            lineHeight: 1.35,
                                        }}>
                                            {dayjs(event.end).format('HH:mm')}
                                        </Typography>
                                    )}
                                </Stack>
                            );
                        })}
                    </Box>
                );
            })}
            </Box>
        </Box>
    );
};

export default MobileAgenda;
